import React from 'react';
import styled from 'styled-components';
import { rgba } from 'polished';
import {
    IconButton,
    InputAdornment,
    InputLabelProps,
    TextField as MuiTextField,
    TextFieldProps as MuiTextFieldProps,
    Tooltip,
    Theme,
} from '@mui/material';
import { Help, KeyboardArrowDown } from '@mui/icons-material';
import { red } from '@modules/ui/colors';
import { concatenateClasses } from '@modules/ui/utils';
import { makeStyles } from '@mui/styles';
import { fontSize, spacing } from '@modules/ui/tokens';

export type TextFieldProps = Omit<MuiTextFieldProps, 'size'> & {
    size?: 'extra-small' | 'small' | 'medium' | 'large';
    tooltip?: React.ReactNode;
};

const heightBySizeMap = {
    'extra-small': spacing.xs,
    small: 'inherit',
    medium: spacing.sm,
    large: 'inherit',
};

const useInputLabelStyles = makeStyles({
    root: {
        fontSize: fontSize.md,
    },
});

const useInputStyles = makeStyles<Theme, { size?: NonNullable<TextFieldProps['size']> }>(theme => ({
    root: {
        '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.primary.main,
        },

        '&$disabled': {
            '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(0, 0, 0, 0.23)',
            },
        },

        '& .MuiSelect-select': {
            display: 'block',
        },
    },

    input: p => ({
        padding: p.size ? heightBySizeMap[p.size] : 'auto',
    }),

    multiline: () => ({
        padding: '1rem',
    }),

    notchedOutline: {
        fontSize: fontSize.md,
    },

    adornedStart: {
        '&$error > div:first-child': {
            color: red[100],
        },
    },

    disabled: {},
    error: {},
}));

const HelpIcon = styled(Help)`
    color: ${p => rgba(p.theme.palette.primary.main, 0.6)};
    font-size: 24px;
`;

const TextField = (props: TextFieldProps): React.ReactElement => {
    const { size, tooltip, SelectProps, InputLabelProps, InputProps = {}, ...otherProps } = props;

    const inputLabelClasses = concatenateClasses(useInputLabelStyles(), InputLabelProps?.classes);
    const inputClasses = concatenateClasses(useInputStyles({ size }), InputProps?.classes);

    const extraInputLabelProps: Partial<InputLabelProps> = {};
    const isDateField = props.type === 'date';

    if (isDateField) {
        extraInputLabelProps.shrink = true;
    }

    const endAdornment = tooltip ? (
        <>
            <InputAdornment position='end'>
                <Tooltip title={tooltip}>
                    <IconButton>
                        <HelpIcon />
                    </IconButton>
                </Tooltip>
            </InputAdornment>

            {InputProps.endAdornment}
        </>
    ) : (
        InputProps.endAdornment
    );

    return (
        <MuiTextField
            InputLabelProps={{
                ...InputLabelProps,
                ...extraInputLabelProps,
                classes: inputLabelClasses,
            }}
            InputProps={{
                ...InputProps,
                classes: inputClasses,
                endAdornment,
            }}
            SelectProps={{
                displayEmpty: true,
                IconComponent: KeyboardArrowDown,
                MenuProps: {
                    anchorOrigin: {
                        vertical: 'bottom',
                        horizontal: 'left',
                    },
                    transformOrigin: {
                        vertical: 'top',
                        horizontal: 'left',
                    },
                },
                ...SelectProps,
            }}
            {...otherProps}
        />
    );
};

export { TextField };
